/**
 * The production consumer of the probe: keeps `--bottom-inset` equal to the
 * probed reach on every element registered with `bottomInsetRef`, so
 * floating UI can clear the bottom furniture with one read:
 *
 *   bottom-[calc(max(var(--bottom-inset,0px),env(safe-area-inset-bottom,0px))+1rem)]
 *
 * Not on `:root`: a custom property there is inherited by every element, so
 * each change (a sheet opening over the composer, say) restyled the whole
 * document, ~55ms on a 4x-throttled CPU, mid-animation. On the readers it
 * restyles only them.
 *
 * Registering also marks the reader `data-bottom-edge="avoid"`, so the probe
 * sees through it: a reader waiting at the unset fallback can sit on the very
 * bar it is clearing, and counting it as cover would keep the inset from ever
 * being published. See probe.ts.
 *
 * A rAF loop gated on the trigger harness: probe only when a trigger has
 * fired since the last frame, or per-frame while a marked bar's transition
 * is in flight. The value is written only when the resolved string changes.
 * Removed rather than set to `0px` when nothing reaches, so `var()`
 * fallbacks stay meaningful.
 */

import {
  BOTTOM_EDGE_ATTR,
  BOTTOM_EDGE_AVOID,
  probeBottomEdge,
} from "@/lib/bottom-edge/probe"
import { createTriggerHarness } from "@/lib/bottom-edge/triggers"

export const BOTTOM_INSET_PROPERTY = "--bottom-inset"

const readers = new Set<HTMLElement>()
let published = ""

function write(element: HTMLElement) {
  if (published) element.style.setProperty(BOTTOM_INSET_PROPERTY, published)
  else element.style.removeProperty(BOTTOM_INSET_PROPERTY)
}

function publish(value: string) {
  published = value
  for (const reader of readers) write(reader)
}

/** The inset currently published, "" when nothing reaches. */
export function publishedBottomInset() {
  return published
}

/**
 * Callback ref for an element whose styles read `var(--bottom-inset)`, on
 * itself or its descendants: sets the value on it and marks it for the probe
 * to see through. Module-level, so its identity never changes.
 */
export function bottomInsetRef(element: HTMLElement | null) {
  if (!element) return
  element.setAttribute(BOTTOM_EDGE_ATTR, BOTTOM_EDGE_AVOID)
  readers.add(element)
  write(element)
  return () => {
    readers.delete(element)
  }
}

/** Starts the loop and returns its teardown. */
export function startBottomEdgeInsetWriter(): () => void {
  if (typeof window === "undefined") return () => {}

  const harness = createTriggerHarness()
  let raf = 0

  const tick = () => {
    raf = requestAnimationFrame(tick)
    // consumePending clears the flag, so it must run even while hot.
    const due = harness.consumePending()
    if (!due && !harness.isHot(performance.now())) return

    const { reach } = probeBottomEdge(harness.markers())
    // Rounded up: a fraction too little leaves a seam of overlap.
    const value = reach > 0 ? `${Math.ceil(reach)}px` : ""
    if (value !== published) publish(value)
  }
  raf = requestAnimationFrame(tick)

  return () => {
    cancelAnimationFrame(raf)
    harness.stop()
    publish("")
  }
}
